/**
 * BudgetStatusBadge Component
 *
 * Badge para status do orçamento com cores semânticas e ícones
 * Baseado no padrão StatusBadge existente no projeto
 */

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

export type BudgetStatus =
  | 'draft'
  | 'sent'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'converted';

interface BudgetStatusConfig {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  className: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const BUDGET_STATUS_CONFIG: Record<BudgetStatus, BudgetStatusConfig> = {
  draft: {
    label: 'Rascunho',
    variant: 'secondary',
    className: 'bg-muted text-muted-foreground border-border',
    icon: FileText,
    description: 'Orçamento em elaboração'
  },
  sent: {
    label: 'Enviado',
    variant: 'default',
    className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    icon: Send,
    description: 'Orçamento enviado ao cliente'
  },
  approved: {
    label: 'Aprovado',
    variant: 'default',
    className: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
    icon: CheckCircle,
    description: 'Orçamento aprovado pelo cliente'
  },
  rejected: {
    label: 'Rejeitado',
    variant: 'destructive',
    className: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
    icon: XCircle,
    description: 'Orçamento rejeitado pelo cliente'
  },
  expired: {
    label: 'Expirado',
    variant: 'outline',
    className: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
    icon: Clock,
    description: 'Validade do orçamento expirada'
  },
  converted: {
    label: 'Convertido',
    variant: 'default',
    className: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
    icon: RefreshCw,
    description: 'Orçamento convertido em venda/contrato'
  }
};

interface BudgetStatusBadgeProps {
  status: BudgetStatus;
  className?: string;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  animated?: boolean;
}

export function BudgetStatusBadge({
  status,
  className,
  showIcon = true,
  size = 'md',
  animated = false
}: BudgetStatusBadgeProps) {
  const config = BUDGET_STATUS_CONFIG[status];

  if (!config) {
    return (
      <Badge variant="outline" className={cn("text-xs", className)}>
        Status desconhecido
      </Badge>
    );
  }

  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 h-5',
    md: 'text-sm px-2.5 py-1 h-6',
    lg: 'text-base px-3 py-1.5 h-7'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  return (
    <Badge
      variant={config.variant}
      className={cn(
        config.className,
        sizeClasses[size],
        'font-medium flex items-center gap-1.5 transition-all duration-200',
        animated && 'animate-pulse',
        className
      )}
      title={config.description}
    >
      {showIcon && (
        <Icon className={cn(iconSizes[size], 'shrink-0')} />
      )}
      {config.label}
    </Badge>
  );
}

// Helper para obter configurações de status (usado por outros componentes)
export function getBudgetStatusConfig(status: BudgetStatus): BudgetStatusConfig {
  return BUDGET_STATUS_CONFIG[status] || {
    label: 'Desconhecido',
    variant: 'outline',
    className: 'bg-muted text-muted-foreground border-border',
    icon: AlertTriangle,
    description: 'Status não reconhecido'
  };
}

// Lista de todos os status disponíveis
export const BUDGET_STATUSES: BudgetStatus[] = [
  'draft',
  'sent',
  'approved',
  'rejected',
  'expired',
  'converted'
] as const;